import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/authApi';
import { tokenStorage } from '@/services/storage';
import { useAuthStore } from '@/stores/authStore';
import { queryKeys } from '@/lib/queryKeys';
import { ROUTES } from '@/config/routes';
import { decodeAccessTokenPermissions, decodeAccessTokenRoles } from '@/lib/jwt';
import type {
  ChangeLanguageRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  RequestOtpRequest,
  ResetPasswordRequest,
  VerifyOtpRequest,
} from '../types/authTypes';

/** Fetches the logged-in user's profile and keeps authStore in sync - the one place that bridges "server truth" (TanStack Query) into "who's logged in right now" (Zustand). */
export function useCurrentUser() {
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);

  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: async () => {
      const response = await authApi.me();
      const user = response.data.data;
      const accessToken = tokenStorage.getAccessToken();
      setCurrentUser({
        publicId: user.publicId,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        accountType: user.accountType,
        status: user.status,
        preferredLanguage: user.preferredLanguage,
        mustChangePassword: user.mustChangePassword,
        roles: accessToken ? decodeAccessTokenRoles(accessToken) : [],
        permissions: accessToken ? decodeAccessTokenPermissions(accessToken) : [],
      });
      return user;
    },
    // Only attempt this if we might actually be logged in - avoids a
    // guaranteed-to-401 request firing on every single public page load.
    enabled: Boolean(tokenStorage.getAccessToken() || tokenStorage.getRefreshToken()),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LoginRequest) => authApi.login(payload),
    onSuccess: (response) => {
      const { accessToken, refreshToken, user } = response.data.data;
      tokenStorage.setAccessToken(accessToken);
      tokenStorage.setRefreshToken(refreshToken);
      setCurrentUser({
        publicId: user.publicId,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        accountType: user.accountType,
        status: user.status,
        preferredLanguage: user.preferredLanguage,
        mustChangePassword: user.mustChangePassword,
        roles: decodeAccessTokenRoles(accessToken),
        permissions: decodeAccessTokenPermissions(accessToken),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useLogout() {
  const clearAuth = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    },
    onSettled: () => {
      tokenStorage.clearAll();
      clearAuth();
      queryClient.clear(); // Section 5.6: "clear sensitive state during logout" - wipe every cached query, not just auth
      navigate(ROUTES.home);
    },
  });
}

export function useRegister() {
  return useMutation({ mutationFn: (payload: RegisterRequest) => authApi.register(payload) });
}

export function useRequestOtp() {
  return useMutation({ mutationFn: (payload: RequestOtpRequest) => authApi.requestOtp(payload) });
}

export function useVerifyOtp() {
  return useMutation({ mutationFn: (payload: VerifyOtpRequest) => authApi.verifyOtp(payload) });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (payload: ForgotPasswordRequest) => authApi.forgotPassword(payload) });
}

export function useResetPassword() {
  return useMutation({ mutationFn: (payload: ResetPasswordRequest) => authApi.resetPassword(payload) });
}

export function useChangeLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ChangeLanguageRequest) => authApi.changeLanguage(payload),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.currentUser }),
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: (payload: ChangePasswordRequest) => authApi.changePassword(payload) });
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'] as const,
    queryFn: () => authApi.listSessions().then((r) => r.data.data),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => authApi.revokeSession(sessionId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });
}
